import React, { Fragment, memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Divider,
  SearchFilterMultiSelect,
  SearchFilterRange,
  SearchFiltersSection,
  Stack
} from 'investira.react.components';
import { SearchFiltersWrapper } from '../../../components/organisms';
import {
  createPessoaTipoMask,
  readEmpresasFilterQuery,
  readPessoaTipoValues,
  removeEmpresasPaginationFromPath,
  serializeEmpresasFilterQuery,
  updateEmpresasFilterQuery
} from '../_shared/empresasFilterQuery';
import {
  EMPRESAS_FILTER_CONTROL,
  getEmpresasFilterConfig
} from '../_shared/empresasFilterRegistry';

const wFilterSections = [
  {
    id: 'classificacao',
    title: 'Classificação',
    fields: ['pessoa_tipo_id']
  },
  {
    id: 'patrimonio-capital-social',
    title: 'Patrimônio e capital social',
    fields: ['pl', 'cs']
  }
];

/** Monta os valores controlados dos filtros a partir da query normalizada. */
function pvCreateFilterValues(pQuery, pFields) {
  return pFields.reduce((pValues, pFilter) => {
    const xValue = pQuery[pFilter.name];

    pValues[pFilter.name] =
      pFilter.control === EMPRESAS_FILTER_CONTROL.MULTI_SELECT
        ? readPessoaTipoValues(xValue)
        : xValue ?? null;

    return pValues;
  }, {});
}

/** Cria o rascunho vazio de acordo com o controle de cada campo. */
function pvCreateEmptyFilterValues(pFields) {
  return pFields.reduce((pValues, pFilter) => {
    pValues[pFilter.name] =
      pFilter.control === EMPRESAS_FILTER_CONTROL.MULTI_SELECT ? [] : null;

    return pValues;
  }, {});
}

/** Conta os campos que possuem valor efetivamente aplicado na URL. */
function pvCountAppliedFilters(pValues, pFields) {
  return pFields.filter(pFilter => {
    const xValue = pValues[pFilter.name];

    return Array.isArray(xValue) ? xValue.length > 0 : xValue != null;
  }).length;
}

/** Converte o valor controlado para número anulável. */
function pvToNumber(pValue) {
  if (pValue === '' || pValue === null || typeof pValue === 'undefined') {
    return null;
  }

  const xValue = Number(pValue);

  return Number.isFinite(xValue) ? xValue : null;
}

/**
 * Renderiza os filtros aplicáveis à listagem de empresas de um setor.
 * A seleção trabalha como rascunho e a URL preserva o bitmask efetivamente aplicado.
 *
 * @param {Object} pProps Propriedades recebidas pelo slot de filtros da listagem.
 * @param {'desktop'|'mobile'} pProps.displayMode Apresentação escolhida pelo template.
 * @returns {JSX.Element} Filtros responsivos de empresas.
 */
const EmpresasSetorFilter = memo(pProps => {
  const { displayMode } = pProps;
  const xLocation = useLocation();
  const xNavigate = useNavigate();
  const xConfig = getEmpresasFilterConfig();
  const xFields = xConfig.fields;
  const xQuery = readEmpresasFilterQuery(xLocation.search);
  const xAppliedValues = pvCreateFilterValues(xQuery, xFields);
  const xAppliedValuesKey = JSON.stringify(xAppliedValues);
  const [draftValues, setDraftValues] = useState(() => xAppliedValues);
  const [filterValidity, setFilterValidity] = useState({});
  const [open, setOpen] = useState(false);
  const xApplyDisabled = Object.values(filterValidity).some(pValid => pValid === false);
  const xAppliedCount = pvCountAppliedFilters(xAppliedValues, xFields);

  /** Sincroniza o rascunho quando os filtros aplicados na URL mudarem. */
  useEffect(() => {
    setDraftValues(JSON.parse(xAppliedValuesKey));
    setFilterValidity({});
  }, [xAppliedValuesKey]);

  /** Atualiza somente o campo editado no rascunho. */
  function pvChangeFilter(pFilter, pValue) {
    setDraftValues(pCurrentValues => ({ ...pCurrentValues, [pFilter.name]: pValue }));
  }

  /** Registra a validade dos inputs numéricos. */
  function pvChangeValidity(pName, pValid) {
    setFilterValidity(pCurrentValidity => {
      if (pCurrentValidity[pName] === pValid) {
        return pCurrentValidity;
      }

      return { ...pCurrentValidity, [pName]: pValid };
    });
  }

  /** Limpa somente o rascunho; a URL muda após a aplicação. */
  function pvClearDraftFilters() {
    setDraftValues(pvCreateEmptyFilterValues(xFields));
    setFilterValidity({});
  }

  /** Abre o diálogo mobile a partir da seleção efetivamente aplicada. */
  function pvOpenMobileFilters() {
    setDraftValues(pvCreateFilterValues(xQuery, xFields));
    setFilterValidity({});
    setOpen(true);
  }

  /** Fecha o diálogo mobile e descarta alterações não aplicadas. */
  function pvCloseMobileFilters() {
    setDraftValues(pvCreateFilterValues(xQuery, xFields));
    setFilterValidity({});
    setOpen(false);
  }

  /** Grava o novo bitmask, preserva a pesquisa e retorna para a primeira página. */
  function pvApplyDraftFilters() {
    if (xApplyDisabled) {
      return;
    }

    const xUpdates = xFields.reduce((pUpdates, pFilter) => {
      const xValue = draftValues[pFilter.name];

      pUpdates[pFilter.name] =
        pFilter.control === EMPRESAS_FILTER_CONTROL.MULTI_SELECT
          ? createPessoaTipoMask(xValue)
          : xValue;

      return pUpdates;
    }, {});
    const xNextQuery = updateEmpresasFilterQuery(xQuery, xUpdates);
    const xSearch = serializeEmpresasFilterQuery(xNextQuery);
    const xPathname = removeEmpresasPaginationFromPath(xLocation.pathname);

    setOpen(false);
    xNavigate(`${xPathname}${xSearch}`);
  }

  /** Renderiza o controle correspondente ao contrato do registry. */
  function pvRenderFilter(pFilter) {
    const xId =
      pFilter.name === 'pessoa_tipo_id'
        ? 'empresas-pessoa-tipo'
        : `empresas-${pFilter.name}`;
    const xValue = draftValues[pFilter.name];

    if (pFilter.control === EMPRESAS_FILTER_CONTROL.MULTI_SELECT) {
      return (
        <SearchFilterMultiSelect
          id={xId}
          label={pFilter.label}
          helperText={pFilter.helperText}
          value={Array.isArray(xValue) ? xValue : []}
          options={pFilter.options}
          visibleOptionCount={6}
          optionsDialogTitle={pFilter.label}
          onChange={pValue => pvChangeFilter(pFilter, pValue)}
        />
      );
    }

    return (
      <SearchFilterRange
        id={xId}
        label={pFilter.label}
        mode={pFilter.direction === 'min' ? 'min-only' : 'max-only'}
        showSlider={Boolean(pFilter.showSlider)}
        value={{
          min: pFilter.direction === 'min' ? pvToNumber(xValue) : null,
          max: pFilter.direction === 'max' ? pvToNumber(xValue) : null
        }}
        min={pFilter.minimum}
        max={pFilter.maximum}
        step={pFilter.step}
        sliderScale={pFilter.sliderScale}
        currency={pFilter.currency}
        onChange={pValue =>
          pvChangeFilter(pFilter, pValue[pFilter.direction === 'min' ? 'min' : 'max'])
        }
        onValidityChange={pValid => pvChangeValidity(pFilter.name, pValid)}
      />
    );
  }

  return (
    <SearchFiltersWrapper
      displayMode={displayMode}
      id="empresas-filtros"
      title="Filtros"
      appliedCount={xAppliedCount}
      open={open}
      applyDisabled={xApplyDisabled}
      onApply={pvApplyDraftFilters}
      onClear={pvClearDraftFilters}
      onOpen={pvOpenMobileFilters}
      onClose={pvCloseMobileFilters}>
      <Stack sx={{ minWidth: 0, px: 0, pb: 1 }}>
        {wFilterSections.map(pSection => {
          const xSectionFields = pSection.fields
            .map(pName => xFields.find(pFilter => pFilter.name === pName))
            .filter(Boolean);

          if (!xSectionFields.length) {
            return null;
          }

          return (
            <SearchFiltersSection
              key={pSection.id}
              id={`empresas-${pSection.id}`}
              title={pSection.title}
              activeFiltersCount={pvCountAppliedFilters(xAppliedValues, xSectionFields)}>
              {xSectionFields.map((pFilter, pIndex) => (
                <Fragment key={pFilter.name}>
                  {pIndex > 0 && <Divider />}
                  {pvRenderFilter(pFilter)}
                </Fragment>
              ))}
            </SearchFiltersSection>
          );
        })}
      </Stack>
    </SearchFiltersWrapper>
  );
});

EmpresasSetorFilter.propTypes = {
  displayMode: PropTypes.oneOf(['desktop', 'mobile']).isRequired
};

EmpresasSetorFilter.displayName = 'EmpresasSetorFilter';

export default EmpresasSetorFilter;
